import { getSeasonRequestContext } from './seasonContext';
import { tokenStorage } from './storage';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

async function parseResponse(response, { raw = false } = {}) {
  if (raw) {
    return response.blob();
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return null;
}

function extractErrorMessage(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && typeof first.msg === 'string') return first.msg;
    return fallback;
  }
  if (typeof detail === 'object' && typeof detail.msg === 'string') return detail.msg;
  return fallback;
}

async function performTokenRefresh() {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ refresh_token: refreshToken })
  });

  if (!response.ok) {
    tokenStorage.clearTokens();
    return false;
  }

  const payload = await response.json();
  tokenStorage.setTokens({
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token
  });
  return true;
}

let pendingRefresh = null;
function refreshAccessToken() {
  if (!pendingRefresh) pendingRefresh = performTokenRefresh().finally(() => { pendingRefresh = null; });
  return pendingRefresh;
}

export async function apiRequest(
  path,
  { method = 'GET', body, headers = {}, auth = true, retry = true, raw = false, season = getSeasonRequestContext() } = {}
) {
  const finalHeaders = { ...headers };
  if (season?.seasonId) finalHeaders['X-Season-ID'] = String(season.seasonId);
  if (season?.archiveEdit) finalHeaders['X-Archive-Edit'] = 'true';
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  if (body !== undefined && !isFormData) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  if (auth) {
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken) {
      finalHeaders.Authorization = `Bearer ${accessToken}`;
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: finalHeaders,
    body:
      body === undefined
        ? undefined
        : isFormData
          ? body
          : JSON.stringify(body)
  });

  if (response.status === 401 && auth && retry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return apiRequest(path, { method, body, headers, auth, retry: false, raw, season });
    }
  }

  if (!response.ok) {
    const errorBody = await parseResponse(response, { raw: false });
    const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;
    const error = new Error(extractErrorMessage(errorBody?.detail, fallbackMessage));
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  return parseResponse(response, { raw });
}

export function apiGet(path, options = {}) {
  return apiRequest(path, { ...options, method: 'GET' });
}

export function apiPost(path, body, options = {}) {
  return apiRequest(path, { ...options, method: 'POST', body });
}

export function apiPatch(path, body, options = {}) {
  return apiRequest(path, { ...options, method: 'PATCH', body });
}

export function apiPut(path, body, options = {}) {
  return apiRequest(path, { ...options, method: 'PUT', body });
}

export function apiDelete(path, options = {}) {
  return apiRequest(path, { ...options, method: 'DELETE' });
}

export function apiPostForm(path, formData, options = {}) {
  return apiRequest(path, { ...options, method: 'POST', body: formData });
}
